import {
  useState,
  useEffect,
  useContext,
  Fragment,
  useLayoutEffect,
} from 'react'
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'

import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import {
  getAccessControlSummary,
  getOrgEventsForReport,
} from '../../utilities/api'

import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'

import {
  formatShortDate,
  formatCurrency,
  formatNumber,
  formatString,
  capitalizeString,
  timezones,
  setColumnsAndRows,
  setHeaders,
  DEFAULTNUMHEADERS,
  createWorkbook,
  saveWorkbook,
  currencyFormat,
  numberFormat,
  percentFormat,
  addWorksheet,
  styleTotalRow,
  styleSubTotalRow,
  getTimezoneDate,
  checkPermission,
} from '../../utilities/helpers'

import Card from 'react-bootstrap/Card'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'
import Table from 'react-bootstrap/Table'

import {
  calculateAccessControlTotals,
  getAccessPassLabels,
  shouldShowOrganizerAccessPassScans,
  flattenAccessControlTicketTypes,
  sortAccessControlSummary,
} from './accessControlSummary'

import { ReportActions } from '../ReportActions'
import { EventSelector } from '../EventSelector'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'

import { useReportRefresh } from '../../hooks/useReportRefresh'

import './report-print.scss'

export default function AccessControlWrapper({ organization, version = 'v1' }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService
  const isV2 = version === 'v2'

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_ACCESS_CONTROL_SUMMARY
    )

  const [events, setEvents] = useState()

  const [selectedEventUUID, setSelectedEventUUID] = useState()

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)

  const [eventAnalytics, setEventAnalytics] = useState(null)

  useLayoutEffect(() => {
    const root = document.getElementById('root')
    root.classList.add('report-page')

    return () => {
      root.classList.remove('report-page')
    }
  }, [])

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    if (!organization?.uuid) return
    showLoading()
    loadEvents(organization.uuid)
  }, [orgPermissionsReady, hasPermission, organization?.uuid])

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedEventUUID) return

      setIsLoadingData(true)

      try {
        const res = await getAccessControlSummary(
          organization.uuid,
          selectedEventUUID
        )

        // Sort the data before setting state
        const sortedData = sortAccessControlSummary(res.data, organization)
        setEventAnalytics(sortedData)
      } catch (error) {
        console.error('Error fetching access control summary:', error)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }

    if (!orgPermissionsReady || !hasPermission) return
    fetchData()
  }, [
    selectedEventUUID,
    organization?.uuid,
    orgPermissionsReady,
    hasPermission,
    refreshToken,
  ])

  const loadEvents = async (organizationUUID) => {
    let res = await getOrgEventsForReport(organizationUUID)

    if (res.status === 200) {
      setEvents(res.data)
      if (isV2 && !selectedEventUUID && res.data?.length > 0) {
        setSelectedEventUUID(res.data[0].uuid)
      }
      hideLoading()
    }
  }

  // Universal PDF Generator using JSON schema
  const UniversalPDFReport = ({ reportData, organizationName }) => {
    const styles = StyleSheet.create({
      page: { padding: 20, fontSize: 8 },
      headerRow: {
        flexDirection: 'row',
        marginBottom: 15,
        alignItems: 'flex-start',
      },
      logoContainer: {
        width: '25%',
        alignItems: 'flex-start',
        justifyContent: 'center',
      },
      logoText: {
        fontSize: 10,
        fontWeight: 'bold',
        backgroundColor: '#EAB308',
        color: 'white',
        padding: 8,
        textAlign: 'center',
      },
      centerContainer: {
        width: '50%',
        alignItems: 'center',
      },
      orgName: {
        fontSize: 16,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 5,
      },
      eventSummary: {
        fontSize: 12,
        textAlign: 'center',
        marginBottom: 3,
      },
      dateRange: {
        fontSize: 10,
        textAlign: 'center',
        color: '#666',
      },
      rightContainer: {
        width: '25%',
        alignItems: 'flex-end',
        justifyContent: 'center',
      },
      printedOn: {
        fontSize: 8,
        color: '#666',
        textAlign: 'right',
      },
      timestamp: {
        fontSize: 8,
        color: '#666',
        textAlign: 'right',
        marginTop: 2,
      },
      table: {
        display: 'table',
        width: '100%',
        marginTop: 20,
      },
      tableRow: {
        flexDirection: 'row',
      },
      headerRowTable: {
        flexDirection: 'row',
        backgroundColor: '#f3f4f6',
      },
      headerText: {
        fontSize: 7,
        fontWeight: 'bold',
      },
      cellText: {
        fontSize: 7,
      },
    })

    const format = (section) => {
      const { sectionNumber, ticketsSold, ticketProceeds, scanned, unScanned } =
        section

      return {
        section_name: sectionNumber,
        tickets_sold: formatNumber(ticketsSold),
        ticket_proceeds: formatCurrency(ticketProceeds),
        scanned: formatNumber(scanned),
        unscanned: formatNumber(unScanned),
        unused: formatPercent(ticketsSold, unScanned),
      }
    }

    const schema = Object.values(reportData).map((section) => format(section))

    // Create column styles dynamically from schema
    const columnStyles = {}
    // schema.columns.forEach((col, index) => {
    //   columnStyles[`col${index}`] = {
    //     width: col.width,
    //     borderStyle: "solid",
    //     borderWidth: 0.5,
    //     padding: 3,
    //   };
    // });

    const dynamicStyles = StyleSheet.create(columnStyles)

    return (
      <Document>
        <Page size="A4" orientation="landscape" style={styles.page}>
          <View style={styles.headerRow}>
            <View style={styles.logoContainer}>
              <Text style={styles.logoText}>LOGO</Text>
            </View>
            <View style={styles.centerContainer}>
              <Text style={styles.orgName}>{organizationName}</Text>
              {/* <Text style={styles.eventSummary}>{schema.title}</Text> */}
            </View>
            <View style={styles.rightContainer}>
              <Text style={styles.printedOn}>Printed On:</Text>
              <Text style={styles.timestamp}>
                {new Date().toLocaleString()}
              </Text>
            </View>
          </View>

          <View style={styles.table}>
            <View style={[styles.tableRow, styles.headerRowTable]}>
              {Object.keys(schema).map((col, index) => (
                <View key={index}>
                  <Text style={styles.headerText}>{formatString(col)}</Text>
                </View>
              ))}
            </View>

            {/* {reportData.map((item, rowIndex) => (
              <View key={rowIndex} style={styles.tableRow}>
                {schema.columns.map((col, colIndex) => (
                  <View key={col.key} style={dynamicStyles[`col${colIndex}`]}>
                    <Text style={styles.cellText}>
                    </Text>
                  </View>
                ))}
              </View>
            ))} */}
          </View>
        </Page>
      </Document>
    )
  }

  // Universal Excel Generator using JSON schema
  const handleExcelExport = async (
    organizationName,
    event,
    reportData,
    totals
  ) => {
    const format = (obj) => {
      const {
        sectionNumber,
        source,
        ticketProceeds,
        ticketsSold,
        scanned,
        unScanned,
        parentSource,
        subGroupParent,
        title,
        name,
        indent,
      } = obj

      const ticketTypeLabel = capitalizeString(title) || capitalizeString(name)
      const ticketTypeIndent = '  '.repeat(Number(indent) || 0)

      return {
        ...(sectionNumber && { section: sectionNumber }),
        ...(source && {
          ticket_type: `${ticketTypeIndent}${ticketTypeLabel}`,
        }),
        tickets_sold: ticketsSold,
        ticket_proceeds: ticketProceeds,
        scanned,
        unscanned: unScanned,
        unused: unScanned / ticketsSold || 0,
        parent_source: parentSource ?? undefined,
        sub_group_parent: subGroupParent ?? false,
      }
    }

    const hideColumn = (worksheet) => {
      worksheet.getColumn('parent_source').hidden = true
      worksheet.getColumn('sub_group_parent').hidden = true
    }

    const getUnusedTotal = (totals) => {
      return totals.unScanned / totals.ticketsSold
    }

    const getColNum = (type) => {
      const soldTotalColumn = 'B'
      const ticketProceedsTotalColumn = 'C'
      const scannedTotalColumn = 'D'
      const unscannedTotalColumn = 'E'
      const unusedTotalColumn = 'F'

      switch (type) {
        case 'sold':
          return soldTotalColumn
        case 'proceeds':
          return ticketProceedsTotalColumn
        case 'scanned':
          return scannedTotalColumn
        case 'unscanned':
          return unscannedTotalColumn
        case 'unused':
          return unusedTotalColumn

        default:
          return
      }
    }

    const getBySectionData = (reportData, formatter, headerRows, worksheet) => {
      const summaryBySectionData = reportData?.attendanceBySection

      const sections = Object.keys(summaryBySectionData)

      setColumnsAndRows(
        Object.keys(formatter(summaryBySectionData[sections[0]])),
        Object.values(summaryBySectionData),
        formatter,
        worksheet
      )

      hideColumn(worksheet)

      const lastRowNum = worksheet.lastRow.number
      const totalRowNum = lastRowNum + 1

      // total row
      worksheet.getCell(`A${totalRowNum}`).value = 'TOTAL'
      worksheet.getCell(`${getColNum('sold')}${totalRowNum}`).value =
        totals.ticketsSold
      worksheet.getCell(`${getColNum('proceeds')}${totalRowNum}`).value =
        totals.ticketProceeds
      worksheet.getCell(`${getColNum('scanned')}${totalRowNum}`).value =
        totals.scanned
      worksheet.getCell(`${getColNum('unscanned')}${totalRowNum}`).value =
        totals.unScanned
      worksheet.getCell(`${getColNum('unused')}${totalRowNum}`).value =
        getUnusedTotal(totals)

      const lastRow = worksheet.lastRow

      styleTotalRow(lastRow, worksheet1, true)

      numberFormat(2, worksheet, headerRows)
      currencyFormat(3, worksheet, headerRows)
      numberFormat(4, worksheet, headerRows)
      numberFormat(5, worksheet, headerRows)
      percentFormat(6, worksheet, headerRows)
    }

    const getByTicketTypeData = (
      reportData,
      formatter,
      headerRows,
      worksheet
    ) => {
      const types = Object.keys(reportData)
      const flattened = flattenAccessControlTicketTypes(reportData)

      setColumnsAndRows(
        Object.keys(formatter(reportData[types[1]])),
        flattened,
        formatter,
        worksheet
      )

      hideColumn(worksheet)

      const getParentSourceValue = (row) => row.getCell('parent_source').value

      const getSubGroupParentSourceValue = (row) =>
        row.getCell('sub_group_parent').value

      // style total rows
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber <= headerRows) return // Skip title and header rows
        const isSubItem = getParentSourceValue(row)
        const isSubGroupParent = getSubGroupParentSourceValue(row)

        // parent group
        if (!isSubItem) {
          styleTotalRow(row, worksheet, true)
          // sub group
        } else if (isSubGroupParent) {
          styleSubTotalRow(row, worksheet, true)
        }
      })

      // total row
      const lastRowNum = worksheet.lastRow.number
      const totalRow = worksheet.getRow(lastRowNum + 1)
      const totalRowNumber = worksheet.getRow(lastRowNum + 1).number

      worksheet.getCell(`A${totalRowNumber}`).value = 'TOTAL'
      worksheet.getCell(`${getColNum('sold')}${totalRowNumber}`).value =
        totals.ticketsSold
      worksheet.getCell(`${getColNum('proceeds')}${totalRowNumber}`).value =
        totals.ticketProceeds
      worksheet.getCell(`${getColNum('scanned')}${totalRowNumber}`).value =
        totals.scanned
      worksheet.getCell(`${getColNum('unscanned')}${totalRowNumber}`).value =
        totals.unScanned
      worksheet.getCell(`${getColNum('unused')}${totalRowNumber}`).value =
        getUnusedTotal(totals)

      styleTotalRow(totalRow, worksheet, true)

      numberFormat(2, worksheet, headerRows)
      currencyFormat(3, worksheet, headerRows)
      numberFormat(4, worksheet, headerRows)
      numberFormat(5, worksheet, headerRows)
      percentFormat(6, worksheet, headerRows)
    }

    const workbook = createWorkbook()
    const worksheet1 = addWorksheet('Summary by Section', workbook)
    const worksheet2 = addWorksheet('Summary by Ticket Type', workbook)
    const headers = [
      `Event: ${event?.name} - ${formatShortDate(getTimezoneDate(event?.start, timezones(event?.venue?.timezone)), 'dateTime')}`,
    ]

    const headerRows = DEFAULTNUMHEADERS + headers?.length

    setHeaders('Summary by Section', organizationName, worksheet1, headers)
    setHeaders('Summary by Ticket Type', organizationName, worksheet2, headers)
    getBySectionData(reportData, format, headerRows, worksheet1)
    getByTicketTypeData(reportData, format, headerRows, worksheet2)

    // Generate and download Excel file
    // save excel worksheet
    saveWorkbook(workbook, 'access control attendance summary')
      .then()
      .catch((err) => alert(err.message))
  }

  const generatePDFFromSchema = async (reportData, organizationName) => {
    const doc = (
      <UniversalPDFReport
        reportData={reportData}
        organizationName={organizationName}
      />
    )

    const blob = await pdf(doc).toBlob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `attendance-summary.pdf`
    link.click()
    URL.revokeObjectURL(url)
  }

  function calculateTotals() {
    return calculateAccessControlTotals(eventAnalytics)
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section
      className={`spacer-sm access-control-report ${isV2 ? 'access-control-report--v2' : ''}`}
      id="analytics-wrapper"
      data-testid="access-control-report-section"
    >
      <header className="section-header-sm report-page-header">
        <div className="section-heading">
          <h1>Access Control Attendance Summary</h1>
        </div>
        <p className="section-header-desc">
          View ticket proceeds, tickets sold or allocated, and attendance data
          for the selected event.
        </p>
      </header>
      {events?.length > 0 ? (
        <>
          <ReportActions
            isLoading={isLoadingData}
            onRefresh={refresh}
            isRefreshing={isLoadingData}
            selected={selectedEventUUID}
            data={eventAnalytics}
            downloadReport={() =>
              handleExcelExport(
                organization?.name,
                events?.find((event) => event?.uuid === selectedEventUUID),
                eventAnalytics,
                calculateTotals()
              )
            }
          >
            <EventSelector
              setSelected={setSelectedEventUUID}
              selected={selectedEventUUID}
              events={events}
              isLoading={isLoadingData}
              groupBySeason
            />
          </ReportActions>
          <Results
            isLoading={isLoadingData}
            selectedEvent={selectedEventUUID}
            data={eventAnalytics}
            calculateTotals={calculateTotals}
            isV2={isV2}
            accessPassLabels={getAccessPassLabels(organization)}
            showAccessPassScans={shouldShowOrganizerAccessPassScans(
              organization
            )}
          />
        </>
      ) : (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>No events created</p>
            <p className="subtitle subtitle--dark">
              Create an event to start tracking its attendance.
            </p>
          </EmptyContainer>
        </Card>
      )}
    </section>
  )
}

const formatPercent = (sold, unscanned) => {
  if (sold > 0) {
    const percentage = ((unscanned / sold) * 100).toFixed(2)
    return `${percentage}%`
  }
  return '0%'
}

// Shared row component
const ItemRow = ({ title, data, indentSize }) => (
  <tr className={`md-x ${indentSize ? `md-x-${indentSize}` : ''} `}>
    <td colSpan={3}>{capitalizeString(title)}</td>
    <td className="whitespace-nowrap">{formatNumber(data.ticketsSold)}</td>
    <td className="whitespace-nowrap">{formatCurrency(data.ticketProceeds)}</td>
    <td className="whitespace-nowrap">{formatNumber(data.scanned)}</td>
    <td className="whitespace-nowrap">{formatNumber(data.unScanned)}</td>
    <td className="whitespace-nowrap">
      {formatPercent(data.ticketsSold, data.unScanned)}
    </td>
  </tr>
)

// Shared table headers
const TableHeader = ({ first }) => (
  <thead>
    <tr className="md">
      <th className="uppercase text-body" colSpan={3}>
        {first}
      </th>
      <th className="uppercase text-body">Tickets Sold / Allocated</th>
      <th className="uppercase text-body">Ticket Proceeds</th>
      <th className="uppercase text-body md">Scanned</th>
      <th className="uppercase text-body lg">Unscanned</th>
      <th className="uppercase text-body md">Unused %</th>
    </tr>
  </thead>
)

// Shared total row
const TotalRow = ({ totals }) => (
  <tr className="total-row dark md">
    <td className="text-uppercase" colSpan={3}>
      Total
    </td>
    <td className="whitespace-nowrap">{formatNumber(totals.ticketsSold)}</td>
    <td className="whitespace-nowrap">
      {formatCurrency(totals.ticketProceeds)}
    </td>
    <td
      className="whitespace-nowrap"
      data-testid="access-control-total-scanned"
    >
      {formatNumber(totals.scanned)}
    </td>
    <td className="whitespace-nowrap">{formatNumber(totals.unScanned)}</td>
    <td className="whitespace-nowrap">
      {formatPercent(totals.ticketsSold, totals.unScanned)}
    </td>
  </tr>
)

function Results({
  isLoading,
  selectedEvent,
  data,
  calculateTotals,
  isV2,
  accessPassLabels,
  showAccessPassScans,
}) {
  const [key, setKey] = useState(isV2 ? 'overview' : 'sections')

  const handleKey = (k) => {
    setKey(k)
  }

  const totals = calculateTotals()

  if (!selectedEvent) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing your filters</p>
          <p className="subtitle subtitle--dark">
            {isV2
              ? 'Choose an event to load sections, ticket types, and top-level attendance metrics.'
              : "Select an event, season or date range from the options above. Once you've made your selections, your report will appear right away."}
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  if (data?.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No data</p>
          <p className="subtitle subtitle--dark">
            There's currently no data for this event. Please choose another
            event.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Tabs
      defaultActiveKey={isV2 ? 'overview' : 'sections'}
      variant="pills"
      activeKey={key}
      onSelect={handleKey}
      className="w-max-content nav-pills-default"
    >
      {isV2 && (
        <Tab eventKey="overview" title="Overview">
          <AccessControlOverview
            eventAnalytics={data}
            totals={totals}
            accessPassLabels={accessPassLabels}
            showAccessPassScans={showAccessPassScans}
          />
        </Tab>
      )}
      <Tab eventKey="sections" title="Summary by Sections">
        <AccessControlSectionsSummary eventAnalytics={data} totals={totals} />
      </Tab>
      <Tab eventKey="ticketType" title="Summary by Ticket Type">
        <AccessControlSummaryTable eventAnalytics={data} totals={totals} />
      </Tab>
    </Tabs>
  )
}

function AccessControlOverview({
  eventAnalytics,
  totals,
  accessPassLabels,
  showAccessPassScans = true,
}) {
  const chartAxisColor = 'rgba(156, 177, 217, 0.88)'
  const chartGridColor = 'rgba(106, 139, 206, 0.2)'
  const chartTitleColor = 'rgba(241, 248, 255, 0.96)'
  const chartTooltipBg = 'rgba(9, 18, 44, 0.94)'

  const sectionRows = Object.values(eventAnalytics?.attendanceBySection || {})
    .filter((section) => section?.sectionNumber)
    .map((section) => {
      const sold = Number(section.ticketsSold || 0)
      const scanned = Number(section.scanned || 0)
      const unScanned = Number(section.unScanned || Math.max(0, sold - scanned))
      const scanRate =
        sold > 0 ? Number(((scanned / sold) * 100).toFixed(1)) : 0

      return {
        ...section,
        ticketsSold: sold,
        scanned,
        unScanned,
        scanRate,
      }
    })
    .sort((a, b) =>
      String(a.sectionNumber).localeCompare(
        String(b.sectionNumber),
        undefined,
        {
          numeric: true,
          sensitivity: 'base',
        }
      )
    )

  const maxTicketsSold = sectionRows.reduce(
    (max, section) => Math.max(max, section.ticketsSold || 0),
    0
  )
  const maxUnScanned = sectionRows.reduce(
    (max, section) => Math.max(max, section.unScanned || 0),
    0
  )

  const getNumericSectionValue = (section, patterns) => {
    const match = Object.entries(section || {}).find(([key, value]) => {
      if (typeof value !== 'number') return false
      return patterns.some((pattern) => pattern.test(key))
    })
    return match ? Number(match[1] || 0) : null
  }

  const isComplimentaryLabel = (value) => {
    const normalized = String(value || '').toLowerCase()
    return (
      normalized.includes('complimentary') ||
      normalized.includes('assignment_type:comp') ||
      /\bcomp\b/.test(normalized)
    )
  }

  const isSeasonLabel = (value) => {
    const normalized = String(value || '').toLowerCase()
    return (
      normalized.includes('season package') ||
      normalized.includes('season ticket') ||
      normalized.includes('season')
    )
  }

  const aggregateNoShowMix = (data) => {
    const totals = {
      sold: 0,
      season: 0,
      complimentary: 0,
    }

    const walk = (group, lineage = []) => {
      if (!group || typeof group !== 'object') return
      const entries = Object.entries(group).filter(
        ([key]) => key !== 'attendanceBySection'
      )

      entries.forEach(([key, value]) => {
        if (!value || typeof value !== 'object') return

        const currentLineage = [
          ...lineage,
          key,
          value.source || '',
          value.title || '',
        ]
        const hasSubGroups =
          value.subGroups && Object.keys(value.subGroups).length > 0

        if (hasSubGroups) {
          walk(value.subGroups, currentLineage)
          return
        }

        const sold = Number(value.ticketsSold || 0)
        if (sold <= 0) return

        const lineageText = currentLineage.join(' ')
        totals.sold += sold

        if (isSeasonLabel(lineageText)) {
          totals.season += sold
        }
        if (isComplimentaryLabel(lineageText)) {
          totals.complimentary += sold
        }
      })
    }

    walk(data)

    return {
      seasonRatio: totals.sold > 0 ? totals.season / totals.sold : 0,
      complimentaryRatio:
        totals.sold > 0 ? totals.complimentary / totals.sold : 0,
    }
  }

  const inferredMix = aggregateNoShowMix(eventAnalytics)

  const staffingRecommendations = sectionRows
    .map((section) => {
      const soldDemandScore =
        maxTicketsSold > 0 ? (section.ticketsSold / maxTicketsSold) * 45 : 0
      const unScannedPressureScore =
        maxUnScanned > 0 ? (section.unScanned / maxUnScanned) * 25 : 0
      const lowScanRateScore = ((100 - section.scanRate) / 100) * 10
      const directSeasonTickets = getNumericSectionValue(section, [
        /season.*ticket.*sold/i,
        /season.*ticket.*count/i,
        /season.*ticket/i,
        /season.*sold/i,
      ])
      const directComplimentaryTickets = getNumericSectionValue(section, [
        /complimentary.*sold/i,
        /complimentary.*ticket/i,
        /comp.*ticket/i,
        /comp.*sold/i,
      ])
      const estimatedSeasonTickets =
        section.ticketsSold * inferredMix.seasonRatio || 0
      const estimatedComplimentaryTickets =
        section.ticketsSold * inferredMix.complimentaryRatio || 0
      const seasonTickets = Math.min(
        section.ticketsSold,
        directSeasonTickets ?? estimatedSeasonTickets
      )
      const complimentaryTickets = Math.min(
        section.ticketsSold,
        directComplimentaryTickets ?? estimatedComplimentaryTickets
      )
      const seasonShare =
        section.ticketsSold > 0 ? seasonTickets / section.ticketsSold : 0
      const complimentaryShare =
        section.ticketsSold > 0 ? complimentaryTickets / section.ticketsSold : 0
      const seasonRiskScore = seasonShare * 8
      const complimentaryRiskScore = complimentaryShare * 12
      const demandScore = Math.min(
        100,
        soldDemandScore +
          unScannedPressureScore +
          lowScanRateScore +
          seasonRiskScore +
          complimentaryRiskScore
      )

      let priorityLabel = 'Monitor'
      if (demandScore >= 70) {
        priorityLabel = 'High Priority'
      } else if (demandScore >= 45) {
        priorityLabel = 'Medium Priority'
      }

      return {
        sectionNumber: section.sectionNumber,
        ticketsSold: section.ticketsSold,
        unScanned: section.unScanned,
        scanRate: section.scanRate,
        demandScore: Number(demandScore.toFixed(1)),
        seasonShare: Number((seasonShare * 100).toFixed(1)),
        complimentaryShare: Number((complimentaryShare * 100).toFixed(1)),
        usedEstimatedMix:
          directSeasonTickets === null || directComplimentaryTickets === null,
        priorityLabel,
      }
    })
    .filter((section) => section.ticketsSold > 0)
    .sort((a, b) => b.demandScore - a.demandScore)
    .slice(0, 5)

  const sectionChartHeight = 365

  const sectionCategories = sectionRows.map((section) => section.sectionNumber)
  const scannedBySection = sectionRows.map((section) => section.scanned)
  const unScannedBySection = sectionRows.map((section) => section.unScanned)
  const unusedBySection = sectionRows.map((section) => {
    const sold = section.ticketsSold
    const unScanned = section.unScanned
    return sold > 0 ? Number(((unScanned / sold) * 100).toFixed(2)) : 0
  })

  const mixSeriesData = Object.entries(eventAnalytics || {})
    .filter(
      ([key, value]) => key !== 'attendanceBySection' && value?.ticketsSold
    )
    .map(([key, value]) => ({
      name: capitalizeString(value?.title || formatString(key)),
      y: value.ticketsSold || 0,
    }))

  const sectionPerformanceOptions = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: sectionChartHeight,
    },
    title: { text: null },
    credits: { enabled: false },
    legend: {
      align: 'left',
      verticalAlign: 'top',
      floating: false,
      itemStyle: {
        color: chartAxisColor,
      },
    },
    xAxis: {
      categories: sectionCategories,
      crosshair: false,
      lineColor: chartGridColor,
      tickColor: chartGridColor,
      labels: {
        style: { color: chartAxisColor },
        rotation: -45,
      },
      title: { text: 'Sections', style: { color: chartAxisColor } },
    },
    yAxis: {
      min: 0,
      gridLineColor: chartGridColor,
      labels: { style: { color: chartAxisColor } },
      title: { text: 'Tickets', style: { color: chartAxisColor } },
      allowDecimals: false,
    },
    tooltip: {
      formatter: function () {
        const scanned = Number(scannedBySection[this.point.index] || 0)
        const unScanned = Number(unScannedBySection[this.point.index] || 0)
        const sold = scanned + unScanned
        const scanRate = sold > 0 ? ((scanned / sold) * 100).toFixed(1) : '0.0'
        return `<b>Section ${this.x}</b><br/>Sold / Allocated: ${formatNumber(sold)}<br/>Scanned: ${formatNumber(scanned)}<br/>Unscanned: ${formatNumber(unScanned)}<br/>Scan rate: ${scanRate}%`
      },
      backgroundColor: chartTooltipBg,
      borderColor: 'rgba(126, 169, 255, 0.32)',
      style: { color: chartTitleColor },
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        borderWidth: 0,
        pointPadding: 0.08,
        groupPadding: 0.1,
      },
      series: {
        states: {
          inactive: {
            opacity: 1,
          },
        },
      },
    },
    series: [
      {
        name: 'Scanned',
        data: scannedBySection,
        color: '#22b07d',
      },
      {
        name: 'Unscanned',
        data: unScannedBySection,
        color: '#e59676',
      },
    ],
  }

  const unusedRateOptions = {
    chart: {
      type: 'column',
      backgroundColor: 'transparent',
      height: 360,
    },
    title: { text: null },
    credits: { enabled: false },
    xAxis: {
      categories: sectionCategories,
      lineColor: chartGridColor,
      tickColor: chartGridColor,
      labels: { style: { color: chartAxisColor } },
      title: { text: 'Sections', style: { color: chartAxisColor } },
    },
    yAxis: {
      min: 0,
      max: 100,
      gridLineColor: chartGridColor,
      title: { text: 'Unused %', style: { color: chartAxisColor } },
      labels: {
        format: '{value}%',
        style: { color: chartAxisColor },
      },
    },
    tooltip: {
      pointFormat: '<b>{point.y:.2f}%</b> unused',
      backgroundColor: chartTooltipBg,
      borderColor: 'rgba(126, 169, 255, 0.32)',
      style: { color: chartTitleColor },
    },
    series: [
      {
        name: 'Unused %',
        data: unusedBySection,
        color: '#eb5757',
      },
    ],
  }

  const ticketMixOptions = {
    chart: {
      type: 'pie',
      backgroundColor: 'transparent',
      height: 360,
    },
    title: { text: null },
    credits: { enabled: false },
    legend: {
      itemStyle: {
        color: chartAxisColor,
      },
    },
    tooltip: {
      pointFormat: '<b>{point.y}</b> tickets ({point.percentage:.1f}%)',
      backgroundColor: chartTooltipBg,
      borderColor: 'rgba(126, 169, 255, 0.32)',
      style: { color: chartTitleColor },
    },
    plotOptions: {
      pie: {
        innerSize: '55%',
        borderColor: 'rgba(255,255,255,0.08)',
        dataLabels: {
          enabled: true,
          format: '{point.name}: {point.percentage:.1f}%',
          color: chartAxisColor,
          style: {
            textOutline: 'none',
          },
        },
      },
    },
    series: [
      {
        name: 'Tickets Sold / Allocated',
        data: mixSeriesData,
      },
    ],
  }

  return (
    <div className="access-control-overview mt-3">
      <div className="access-control-overview__cards">
        <Card
          body
          className="access-control-overview__metric access-control-overview__metric--teal"
        >
          <div className="caption text-muted">Total Sold / Allocated</div>
          <h3 className="mb-0">{formatNumber(totals.ticketsSold)}</h3>
        </Card>
        <Card
          body
          className="access-control-overview__metric access-control-overview__metric--green"
        >
          <div className="caption text-muted">Total Scanned</div>
          <h3 className="mb-0">{formatNumber(totals.scanned)}</h3>
        </Card>
        <Card
          body
          className="access-control-overview__metric access-control-overview__metric--purple"
        >
          <div className="caption text-muted">Scan Rate</div>
          <h3 className="mb-0">
            {totals.ticketsSold > 0
              ? `${((totals.scanned / totals.ticketsSold) * 100).toFixed(2)}%`
              : '0%'}
          </h3>
        </Card>
        <Card
          body
          className="access-control-overview__metric access-control-overview__metric--amber"
        >
          <div className="caption text-muted">Ticket Proceeds</div>
          <h3 className="mb-0">{formatCurrency(totals.ticketProceeds)}</h3>
        </Card>
        {showAccessPassScans && (
          <Card
            body
            className="access-control-overview__metric access-control-overview__metric--pink"
          >
            <div className="caption text-muted">
              {accessPassLabels?.scans || 'Access Pass Scans'}
            </div>
            <h3 className="mb-0" data-testid="access-pass-scan-count-overview">
              {formatNumber(totals.accessPassScanCount)}
            </h3>
          </Card>
        )}
      </div>

      <Card
        body
        className="access-control-overview__panel access-control-overview__recommendation mb-3"
      >
        <Card.Title as="h5" className="mb-2">
          Staffing Recommendation
        </Card.Title>
        <p className="subtitle subtitle--dark mb-3">
          Prioritize usher coverage in sections with the highest demand pressure
          using sold or allocated volume, current unscanned counts, scan rate,
          season ticket mix, and complimentary ticket mix.
        </p>
        {staffingRecommendations.length > 0 ? (
          <ul className="access-control-overview__recommendation-list mb-0">
            {staffingRecommendations.map((section) => (
              <li key={section.sectionNumber}>
                <span className="access-control-overview__recommendation-priority">
                  {section.priorityLabel}
                </span>
                <span className="access-control-overview__recommendation-section">
                  Section {section.sectionNumber}
                </span>
                <span className="access-control-overview__recommendation-meta">
                  Demand score: {section.demandScore} / 100
                </span>
                <span className="access-control-overview__recommendation-meta">
                  Sold / Allocated: {formatNumber(section.ticketsSold)}
                </span>
                <span className="access-control-overview__recommendation-meta">
                  Unscanned: {formatNumber(section.unScanned)}
                </span>
                <span className="access-control-overview__recommendation-meta">
                  Scan rate: {section.scanRate}%
                </span>
                <span className="access-control-overview__recommendation-meta">
                  Season Ticket mix: {section.seasonShare}%
                </span>
                <span className="access-control-overview__recommendation-meta">
                  Complimentary Ticket mix: {section.complimentaryShare}%
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyContainer style="center lg report">
            <p>No staffing recommendation available yet</p>
          </EmptyContainer>
        )}
        {(inferredMix.seasonRatio > 0 ||
          inferredMix.complimentaryRatio > 0) && (
          <p className="subtitle subtitle--dark mb-0 mt-3">
            Mix note: when section-level season or complimentary counts are not
            present, estimates use event-level ticket mix.
          </p>
        )}
      </Card>

      {sectionRows.length > 0 ? (
        <div className="access-control-overview__charts">
          <Card body className="access-control-overview__panel">
            <Card.Title as="h5" className="mb-2">
              Scanned vs Unscanned breakdown
            </Card.Title>
            <p className="subtitle subtitle--dark mb-3">
              Compare attendance completion by section with stacked totals.
            </p>
            <HighchartsReact
              highcharts={Highcharts}
              options={sectionPerformanceOptions}
            />
          </Card>

          <Card body className="access-control-overview__panel">
            <Card.Title as="h5" className="mb-2">
              Ticket Mix
            </Card.Title>
            <p className="subtitle subtitle--dark mb-3">
              Distribution of sold or allocated tickets by source group.
            </p>
            {mixSeriesData.length > 0 ? (
              <HighchartsReact
                highcharts={Highcharts}
                options={ticketMixOptions}
              />
            ) : (
              <EmptyContainer style="center lg report">
                <p>No ticket mix data available</p>
              </EmptyContainer>
            )}
          </Card>

          <Card
            body
            className="access-control-overview__panel access-control-overview__panel--full"
          >
            <Card.Title as="h5" className="mb-2">
              Unused Percentage by Section
            </Card.Title>
            <p className="subtitle subtitle--dark mb-3">
              Sections with higher unused percentages may need follow-up.
            </p>
            <HighchartsReact
              highcharts={Highcharts}
              options={unusedRateOptions}
            />
          </Card>
        </div>
      ) : (
        <Card body>
          <EmptyContainer style="center lg report">
            <p>No section data available</p>
            <p className="subtitle subtitle--dark">
              Visualizations will appear once section-level attendance data is
              available.
            </p>
          </EmptyContainer>
        </Card>
      )}
    </div>
  )
}

function AccessControlSectionsSummary({ eventAnalytics, totals }) {
  if (!eventAnalytics?.attendanceBySection) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No section data available</p>
          <p className="subtitle subtitle--dark">
            Section-based analytics will be displayed here once available.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  const sections = eventAnalytics.attendanceBySection

  return (
    <Card body className="card--md">
      <Card.Title as="h5" className="mb-3">
        Summary by Sections
      </Card.Title>
      <div className="table-container border-bottom-0">
        <div className="table-container--fixed-height">
          <Table>
            <TableHeader first={'Section'} />
            <tbody>
              {Object.entries(sections).map(([key, sectionData]) => (
                <ItemRow
                  key={key}
                  title={sectionData.sectionNumber || key}
                  data={sectionData}
                />
              ))}
            </tbody>
            <tfoot>
              <TotalRow totals={totals} />
            </tfoot>
          </Table>
        </div>
      </div>
    </Card>
  )
}

function AccessControlSummaryTable({ eventAnalytics, totals }) {
  const SectionRow = ({ title, data }) => {
    if (data?.ticketProceeds === undefined) return null
    return (
      <tr className="total-row dark md-x">
        <td colSpan={3}>{capitalizeString(title)}</td>
        <td className="whitespace-nowrap">{formatNumber(data.ticketsSold)}</td>
        <td className="whitespace-nowrap">
          {formatCurrency(data.ticketProceeds)}
        </td>
        <td className="whitespace-nowrap">{formatNumber(data.scanned)}</td>
        <td className="whitespace-nowrap">{formatNumber(data.unScanned)}</td>
        <td className="whitespace-nowrap">
          {formatPercent(data.ticketsSold, data.unScanned)}
        </td>
      </tr>
    )
  }

  const SubsectionRow = ({ title, data }) => (
    <tr className="total-row light md-x md-x-sm">
      <td colSpan={3}>{capitalizeString(title)}</td>
      <td className="whitespace-nowrap">{formatNumber(data.ticketsSold)}</td>
      <td className="whitespace-nowrap">
        {formatCurrency(data.ticketProceeds)}
      </td>
      <td className="whitespace-nowrap">{formatNumber(data.scanned)}</td>
      <td className="whitespace-nowrap">{formatNumber(data.unScanned)}</td>
      <td className="whitespace-nowrap">
        {formatPercent(data.ticketsSold, data.unScanned)}
      </td>
    </tr>
  )

  if (!eventAnalytics) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>No ticket type data available</p>
          <p className="subtitle subtitle--dark">
            Type-based analytics will be displayed here once available.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <Card.Title as="h5" className="mb-3">
        Summary by Ticket Type
      </Card.Title>
      <div className="table-container border-bottom-0">
        <div className="table-container--fixed-height">
          <Table>
            <TableHeader first={'Ticket Type'} />
            <tbody>
              {Object.entries(eventAnalytics)
                .filter(
                  ([sectionKey]) =>
                    sectionKey !== 'accessPassScanCount' &&
                    sectionKey !== 'attendanceBySection' &&
                    sectionKey !== 'complimentaryTicketCounts' &&
                    sectionKey !== 'countedTicketIds' &&
                    sectionKey !== 'offerSales' &&
                    sectionKey !== 'channelProceeds'
                )
                .map(([sectionKey, sectionData]) => (
                  <Fragment key={sectionKey}>
                    <SectionRow
                      title={sectionData?.title || sectionKey}
                      data={sectionData}
                    />
                    {sectionData.subGroups &&
                      Object.entries(sectionData.subGroups).map(
                        ([subKey, subData]) => (
                          <Fragment key={`${sectionKey}-${subKey}`}>
                            {subData.subGroups &&
                            Object.keys(subData.subGroups).length > 0 ? (
                              <>
                                <SubsectionRow title={subKey} data={subData} />
                                {Object.entries(subData.subGroups).map(
                                  ([itemKey, itemData]) => (
                                    <ItemRow
                                      key={`${sectionKey}-${subKey}-${itemKey}`}
                                      title={itemKey}
                                      data={itemData}
                                      indentSize="lg"
                                    />
                                  )
                                )}
                              </>
                            ) : (
                              <ItemRow
                                title={subKey}
                                data={subData}
                                indentSize="sm"
                              />
                            )}
                          </Fragment>
                        )
                      )}
                  </Fragment>
                ))}
            </tbody>
            <tfoot>
              <TotalRow totals={totals} />
            </tfoot>
          </Table>
        </div>
      </div>
    </Card>
  )
}
